import React, { useEffect, useMemo, useState } from 'react';
import AppHeader from './layout/AppHeader';
import { formatBackendDetail } from '../lib/apiErrors';
import ConsultantBriefing from './ConsultantBriefing';
import './SalesDemoWorkspace.css';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/sales-demo`;
const DEMO_NOTICE = 'Sales demo — fictional data and simulated results.';
const newRequestId = (kind) => {
  const entropy = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `sales-demo:${kind}:${entropy}`;
};

async function readResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(formatBackendDetail(data.detail) || `Request failed (${response.status})`);
  }
  return data;
}

function StrengthBadge({ strength }) {
  return <span className={`sales-demo-strength is-${strength}`}>{strength}</span>;
}

export default function SalesDemoWorkspace() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [strength, setStrength] = useState('all');
  const [resetConfirm, setResetConfirm] = useState(false);

  const workspace = payload?.workspace || {};
  const scenarios = payload?.scenarios || [];
  const selectedScenario = workspace.selected_scenario_id || scenarios[0]?.id || '';

  const load = async () => {
    setError('');
    try {
      setPayload(await readResponse(await fetch(`${API}/session`)));
    } catch (e) {
      setError(e.message || 'The sales-demo workspace could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runScenario = async (scenarioId) => {
    setBusy('simulate'); setError(''); setResetConfirm(false);
    try {
      const body = {
        expected_generation: workspace.generation,
        request_id: newRequestId('simulate'),
      };
      setPayload(await readResponse(await fetch(`${API}/scenarios/${encodeURIComponent(scenarioId)}/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })));
      setStrength('all');
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };

  const changeStatus = async (opportunityId, status) => {
    setBusy(`status:${opportunityId}`); setError('');
    try {
      setPayload(await readResponse(await fetch(`${API}/opportunities/${encodeURIComponent(opportunityId)}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status,
          expected_generation: workspace.generation,
          expected_revision: workspace.state_revision,
        }),
      })));
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };

  const reset = async () => {
    setBusy('reset'); setError('');
    try {
      setPayload(await readResponse(await fetch(`${API}/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scenario_id: selectedScenario,
          expected_generation: workspace.generation,
          request_id: newRequestId('reset'),
        }),
      })));
      setStrength('all');
      setResetConfirm(false);
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };

  const opportunities = useMemo(() => {
    const rows = workspace.opportunities || [];
    return strength === 'all' ? rows : rows.filter((row) => row.strength === strength);
  }, [workspace.opportunities, strength]);

  if (loading) {
    return <><AppHeader active="sales-demo" /><main className="page-shell sales-demo-shell"><p>Loading sales demo…</p></main></>;
  }

  return (
    <>
      <AppHeader active="sales-demo" />
      <main className="page-shell sales-demo-shell" data-testid="sales-demo-workspace">
        <section className="sales-demo-banner" role="status" data-testid="sales-demo-notice">
          <strong>{payload?.workspace_notice || DEMO_NOTICE}</strong>
          <span>No live research, provider calls, outreach, scheduling or customer data.</span>
        </section>

        <div className="sales-demo-heading">
          <div>
            <p className="sales-demo-kicker">Guided Finder workspace</p>
            <h1>Commission-sales demonstration</h1>
            <p>Target market → simulated discovery → supporting intelligence → prioritised next action.</p>
          </div>
          <div className="sales-demo-zero-cost" data-testid="sales-demo-cost">
            <span>Recorded application cost</span><strong>$0.00</strong>
          </div>
        </div>

        {error && <div className="sales-demo-error" role="alert" data-testid="sales-demo-error">{error}</div>}

        <section className="panel sales-demo-scenarios" aria-labelledby="sales-demo-scenario-title">
          <div className="panel-header" id="sales-demo-scenario-title">1. Choose the approved scenario</div>
          <div className="panel-body sales-demo-scenario-grid">
            {scenarios.map((scenario) => (
              <article key={scenario.id} className={`sales-demo-scenario ${selectedScenario === scenario.id ? 'is-selected' : ''}`}>
                <span>{scenario.journey}</span>
                <h2>{scenario.title}</h2>
                <p>{scenario.problem}</p>
                <div className="sales-demo-contract">
                  {Object.values(scenario.contract || {}).join(' → ')}
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!!busy}
                  onClick={() => runScenario(scenario.id)}
                  data-testid={`sales-demo-run-${scenario.id}`}
                >
                  {busy === 'simulate' && selectedScenario === scenario.id ? 'Preparing…' : 'Run simulated discovery'}
                </button>
              </article>
            ))}
          </div>
        </section>

        <section className="panel" aria-labelledby="sales-demo-results-title">
          <div className="panel-header sales-demo-results-header">
            <span id="sales-demo-results-title">2. Explain and prioritise opportunities</span>
            <label>Confidence
              <select value={strength} onChange={(e) => setStrength(e.target.value)} data-testid="sales-demo-filter">
                <option value="all">All</option><option value="strong">Strong</option>
                <option value="uncertain">Uncertain</option><option value="weak">Weak</option>
              </select>
            </label>
          </div>
          <div className="panel-body">
            {workspace.run_status !== 'COMPLETE' && (
              <div className="sales-demo-empty" data-testid="sales-demo-empty">
                Select a scenario to replay its fixed, illustrative discovery results. Nothing will be sent externally.
              </div>
            )}
            <div className="sales-demo-opportunities" data-testid="sales-demo-results">
              {opportunities.map((opportunity) => (
                <article className="sales-demo-opportunity" key={opportunity.id} data-testid={`sales-demo-opportunity-${opportunity.id}`}>
                  <header>
                    <div><StrengthBadge strength={opportunity.strength} /><h3>{opportunity.organisation}</h3></div>
                    <div className="sales-demo-score"><strong>{opportunity.score}</strong><span>/ 100</span></div>
                  </header>
                  <h4>{opportunity.headline}</h4>
                  <dl>
                    <div><dt>Why now</dt><dd>{opportunity.why_now}</dd></div>
                    <div><dt>Uncertainty</dt><dd>{opportunity.uncertainty}</dd></div>
                    <div><dt>Supported next action</dt><dd>{opportunity.next_action}</dd></div>
                  </dl>
                  <details>
                    <summary>View illustrative evidence</summary>
                    {(opportunity.evidence || []).map((item) => (
                      <blockquote key={item.id}>
                        <strong>{item.source_label}</strong>
                        <p>{item.excerpt}</p>
                        <small>Illustrative · fixed demo date {item.observed_on}</small>
                      </blockquote>
                    ))}
                  </details>
                  <ConsultantBriefing
                    intelligence={opportunity.intelligence_v2}
                    allowFeedback={false}
                    compact
                  />
                  <label className="sales-demo-status">Synthetic opportunity status
                    <select
                      value={opportunity.status}
                      disabled={!!busy}
                      onChange={(e) => changeStatus(opportunity.id, e.target.value)}
                      data-testid={`sales-demo-status-${opportunity.id}`}
                    >
                      <option value="new">New</option><option value="reviewing">Reviewing</option>
                      <option value="qualified">Qualified</option><option value="monitor">Monitor</option>
                      <option value="dismissed">Dismissed</option>
                    </select>
                  </label>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="panel sales-demo-guide" aria-labelledby="sales-demo-guide-title">
          <div className="panel-header" id="sales-demo-guide-title">3. Presenter guide · 10–15 minutes</div>
          <div className="panel-body">
            <ol>
              <li><strong>Set the problem (2 min):</strong> agree the target market and explain that today’s data is visibly simulated.</li>
              <li><strong>Run discovery (2 min):</strong> replay one approved scenario; explain that a live evaluation would use Finder’s governed research workflow.</li>
              <li><strong>Read the evidence (4 min):</strong> contrast strong, uncertain and weak signals. A technology mention alone is not buying intent.</li>
              <li><strong>Prioritise (3 min):</strong> filter the cards and make one reversible synthetic status change.</li>
              <li><strong>Agree the next step (2 min):</strong> propose a customer-controlled evaluation with their target market, access and cost boundaries.</li>
            </ol>
            <p><strong>Accidental click?</strong> This workspace cannot contact anyone or spend provider budget. Use the reset below before the next call.</p>
          </div>
        </section>

        <section className="sales-demo-reset" aria-label="Reset synthetic workspace">
          <div><strong>Reset this salesperson’s demo only</strong><span>Restores the selected fixture baseline; audit history is preserved.</span></div>
          {!resetConfirm ? (
            <button className="btn btn-outline" type="button" onClick={() => setResetConfirm(true)} data-testid="sales-demo-reset">Reset demo</button>
          ) : (
            <div className="sales-demo-reset-confirm" role="group" aria-label="Confirm reset">
              <button className="btn btn-primary" type="button" disabled={!!busy} onClick={reset} data-testid="sales-demo-reset-confirm">
                {busy === 'reset' ? 'Resetting…' : 'Confirm reset'}
              </button>
              <button className="btn btn-outline" type="button" disabled={!!busy} onClick={() => setResetConfirm(false)}>Cancel</button>
            </div>
          )}
        </section>

        <footer className="sales-demo-footer">
          Fixture {payload?.fixture_version} · workspace {payload?.workspace_identity} · generation {workspace.generation}
        </footer>
      </main>
    </>
  );
}
